import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/** sessionStorage key: the size handed over to the 18K / 14K counterpart when the karat is switched. */
const STORAGE_KEY = 'purelia:ring-size';

/**
 * sessionStorage key: the size picked per product ({ [productId]: "EU 52" }) for as long as the tab
 * is open, so it is still selected when the customer comes back from the ring sizer (browser back,
 * "Back to Purelia") or opens the product again.
 */
const SESSION_SIZES_KEY = 'purelia:ring-sizes';

/** Clicks on these elements (inside the product form) need a size and a confirmation. */
const BUY_TARGET_SELECTOR = 'add-to-cart-component, .accelerated-checkout-block';

/** URL parameter the ring sizer app adds when it sends the customer back, e.g. ?ring_size=52. */
const SIZE_PARAM = 'ring_size';

/** URL parameter that hands the size picked here to the ring sizer app, e.g. ?size=52. */
const SIZER_SIZE_PARAM = 'size';

/** Digits typed within this time build one size, e.g. "5" then "2" → 52. */
const TYPEAHEAD_RESET_MS = 700;

/**
 * Ring size dropdown + confirmation popup (blocks/ring-size.liquid).
 *
 * The buy buttons of the same product (Add to cart, Buy it now, and the sticky bar, which clicks
 * Add to cart) are intercepted in the capture phase on `window`, before the theme's and Shopify's
 * own handlers. Without a size the click is dropped. With a size the popup opens, and Confirm
 * replays the original click once. The size reaches the cart through the hidden input tied to the
 * buy-buttons form (`form` attribute), so product-form.js needs no changes.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} value
 * @property {HTMLUListElement} listbox
 * @property {HTMLLIElement[]} options
 * @property {HTMLInputElement} input
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} [dialogSizes]
 * @property {HTMLAnchorElement} [sizerLink]
 *
 * @extends {Component<Refs>}
 */
class RingSizePicker extends Component {
  requiredRefs = ['trigger', 'value', 'listbox', 'options', 'input', 'dialog'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** The clicked buy element waiting for Confirm. @type {HTMLElement | null} */
  #pendingTarget = null;

  /** True only while Confirm replays the click. */
  #replaying = false;

  #activeIndex = -1;
  #typeahead = '';
  /** @type {number | undefined} */
  #typeaheadTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
    // Later steps win: the size remembered in this tab, then the 18K / 14K handover, then ?ring_size=.
    this.#restoreSessionSize();
    this.#restoreSize();
    this.#applySizeFromUrl();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    clearTimeout(this.#typeaheadTimer);
  }

  /** The chosen size, e.g. "EU 52", or an empty string. */
  get selectedValue() {
    return this.refs.input.disabled ? '' : this.refs.input.value;
  }

  /** Bound in markup: the trigger button. */
  toggleList() {
    if (this.#isListOpen) {
      this.#closeList();
    } else {
      this.#openList();
    }
  }

  /**
   * Bound in markup: arrows on the closed trigger open the list (Enter and Space click it).
   * @param {KeyboardEvent} event
   */
  handleTriggerKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    this.#openList();
  }

  /**
   * Keyboard navigation inside the open list. Bound directly (not through on:keydown, which runs on
   * document): Escape must stop here, before the quick add dialog sees it and closes.
   * @param {KeyboardEvent} event
   */
  #onListKeydown = (event) => {
    const lastIndex = this.refs.options.length - 1;

    switch (event.key) {
      case 'ArrowDown':
        this.#setActive(this.#activeIndex + 1);
        break;
      case 'ArrowUp':
        this.#setActive(this.#activeIndex - 1);
        break;
      case 'PageDown':
        this.#setActive(this.#activeIndex + 5);
        break;
      case 'PageUp':
        this.#setActive(this.#activeIndex - 5);
        break;
      case 'Home':
        this.#setActive(0);
        break;
      case 'End':
        this.#setActive(lastIndex);
        break;
      case 'Enter':
      case ' ':
        this.#select(this.#activeIndex);
        this.#closeList(true);
        break;
      case 'Escape':
        this.#closeList(true);
        break;
      case 'Tab':
        this.#closeList();
        return;
      default:
        if (/^\d$/.test(event.key)) {
          this.#jumpToTyped(event.key);
          break;
        }
        return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Bound in markup on every option.
   * @param {MouseEvent} event
   */
  handleOptionClick(event) {
    const option = /** @type {HTMLLIElement} */ (event.target);
    this.#select(this.refs.options.indexOf(option));
    this.#closeList(true);
  }

  /** Bound in markup: the popup's Confirm button. Replays the intercepted click once. */
  handleConfirm() {
    const target = this.#pendingTarget;
    this.#pendingTarget = null;
    this.refs.dialog.close();

    if (!target?.isConnected) return;

    // click() runs the click handlers and the form submit synchronously, so the flag covers both.
    this.#replaying = true;
    try {
      target.click();
    } finally {
      this.#replaying = false;
    }
  }

  /** Bound in markup: the ring sizer link in the popup. */
  handleSizerLinkClick() {
    if (this.refs.sizerLink) this.#prepareSizerLink(this.refs.sizerLink);
  }

  /**
   * The help line under the label is merchant richtext, so its ring sizer link can't carry markup.
   * A click on a link to the sizer's site gets the same treatment as the popup link, in this tab.
   * @param {MouseEvent} event
   */
  #onHelpClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('.ring-size__help a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || !this.#isSizerLink(link)) return;

    link.removeAttribute('target');
    this.#prepareSizerLink(link);
  };

  /** @param {HTMLAnchorElement} link */
  #isSizerLink(link) {
    const { sizerUrl } = this.dataset;
    if (!sizerUrl) return false;
    try {
      const sizerOrigin = new URL(sizerUrl, window.location.origin).origin;
      return sizerOrigin !== window.location.origin && new URL(link.href).origin === sizerOrigin;
    } catch {
      return false;
    }
  }

  /**
   * Tells the sizer where to send the customer back (this product with the variant selected in the
   * form, also from quick add), the storefront language, and the size already picked here, so the
   * sizer can open on it for a double check.
   * @param {HTMLAnchorElement} link
   */
  #prepareSizerLink(link) {
    const { productUrl: productPath, lang } = this.dataset;
    if (!productPath) return;

    // The product path on the current host: the shop domain live, 127.0.0.1:9292 under theme dev.
    const productUrl = new URL(productPath, window.location.origin);
    const variantId = this.#form?.querySelector('input[name="id"]')?.value;
    if (variantId) productUrl.searchParams.set('variant', variantId);

    const sizerUrl = new URL(link.href);
    sizerUrl.searchParams.set('return', productUrl.toString());
    if (lang && !sizerUrl.searchParams.has('lang')) sizerUrl.searchParams.set('lang', lang);

    const size = this.selectedValue.replace(/\D/g, '');
    if (size) {
      sizerUrl.searchParams.set(SIZER_SIZE_PARAM, size);
    } else {
      sizerUrl.searchParams.delete(SIZER_SIZE_PARAM);
    }
    link.href = sizerUrl.toString();
  }

  /** Bound in markup: the popup's Edit size button. */
  handleEdit() {
    this.refs.dialog.close();
    this.refs.trigger.focus();
  }

  #listen() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { dialog, listbox } = this.refs;

    window.addEventListener('click', this.#onBuyClick, { capture: true, signal });
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
    // Capture: runs before gold-karat-selector navigates to the other product.
    document.addEventListener('change', this.#onKaratSwitch, { capture: true, signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('click', this.#onHelpClick, { signal });
    listbox.addEventListener('keydown', this.#onListKeydown, { signal });
    dialog.addEventListener('keydown', this.#onDialogKeydown, { signal });
    dialog.addEventListener('click', this.#onDialogClick, { signal });
    dialog.addEventListener('close', this.#onDialogClose, { signal });
  }

  /** The product section, or the quick add modal content when the block sits in quick add. */
  get #root() {
    return this.closest('.quick-add-modal__content, .shopify-section') ?? document.body;
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    return this.#root.querySelector('product-form-component form');
  }

  /**
   * The element to click again after Confirm, when the event hits a buy button of this product.
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #getBuyTarget(event) {
    const root = this.#root;
    const path = event.composedPath();
    const isInsideBuyArea = path.some(
      (node) =>
        node instanceof Element &&
        root.contains(node) &&
        node.matches(BUY_TARGET_SELECTOR) &&
        node.closest('product-form-component')
    );
    if (!isInsideBuyArea) return null;

    // The innermost element: Buy it now may render its real button inside a shadow root.
    const target = path[0];
    return target instanceof HTMLElement ? target : null;
  }

  /** @param {MouseEvent} event */
  #onBuyClick = (event) => {
    if (this.#replaying) return;

    const target = this.#getBuyTarget(event);
    if (!target) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.selectedValue) {
      // Reached through the sticky bar, which clicks Add to cart programmatically.
      this.refs.trigger.focus();
      return;
    }

    this.#pendingTarget = target;
    this.#openDialog();
  };

  /**
   * Covers submits that don't come from a button click (e.g. a script calling requestSubmit()).
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (this.#replaying || event.target !== this.#form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #openDialog() {
    for (const size of this.refs.dialogSizes ?? []) {
      size.textContent = this.selectedValue;
    }
    this.refs.dialog.showModal();
  }

  /**
   * The native dialog closes itself on Escape. Stop the key here so the quick add dialog around
   * this block doesn't close too.
   * @param {KeyboardEvent} event
   */
  #onDialogKeydown = (event) => {
    if (event.key === 'Escape') event.stopPropagation();
  };

  /**
   * A click on the backdrop closes the popup. It must not reach the quick add dialog, which treats it
   * as a click outside itself. Clicks on the buttons keep bubbling: their on:click runs on document.
   * @param {MouseEvent} event
   */
  #onDialogClick = (event) => {
    const { dialog } = this.refs;
    if (event.target !== dialog) return;

    event.stopPropagation();
    if (isClickedOutside(event, dialog)) dialog.close();
  };

  #onDialogClose = () => {
    this.#pendingTarget = null;
  };

  get #isListOpen() {
    return !this.refs.listbox.hidden;
  }

  #openList() {
    const { listbox, trigger, options } = this.refs;

    listbox.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    const selectedIndex = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    this.#setActive(Math.max(selectedIndex, 0));
    this.#centerOption(options[this.#activeIndex]);
    listbox.focus({ preventScroll: true });
  }

  /** @param {boolean} [focusTrigger] */
  #closeList(focusTrigger = false) {
    const { listbox, trigger } = this.refs;
    if (!this.#isListOpen) return;

    listbox.hidden = true;
    listbox.removeAttribute('aria-activedescendant');
    trigger.setAttribute('aria-expanded', 'false');
    if (focusTrigger) trigger.focus();
  }

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (this.#isListOpen && event.target instanceof Node && !this.contains(event.target)) {
      this.#closeList();
    }
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#closeList();
  };

  /** @param {number} index */
  #setActive(index) {
    const { options, listbox } = this.refs;
    const clamped = Math.min(Math.max(index, 0), options.length - 1);
    const option = options[clamped];
    if (!option) return;

    options[this.#activeIndex]?.classList.remove('is-active');
    option.classList.add('is-active');
    this.#activeIndex = clamped;
    listbox.setAttribute('aria-activedescendant', option.id);

    // Scroll only the list, never the page.
    const { offsetTop, offsetHeight } = option;
    if (offsetTop < listbox.scrollTop) {
      listbox.scrollTop = offsetTop;
    } else if (offsetTop + offsetHeight > listbox.scrollTop + listbox.clientHeight) {
      listbox.scrollTop = offsetTop + offsetHeight - listbox.clientHeight;
    }
  }

  /** @param {HTMLElement | undefined} option */
  #centerOption(option) {
    if (!option) return;
    const { listbox } = this.refs;
    listbox.scrollTop = option.offsetTop - (listbox.clientHeight - option.offsetHeight) / 2;
  }

  /** @param {string} digit */
  #jumpToTyped(digit) {
    clearTimeout(this.#typeaheadTimer);
    this.#typeahead += digit;
    this.#typeaheadTimer = setTimeout(() => {
      this.#typeahead = '';
    }, TYPEAHEAD_RESET_MS);

    const index = this.refs.options.findIndex((option) =>
      (option.dataset.value ?? '').replace(/\D/g, '').startsWith(this.#typeahead)
    );
    if (index >= 0) this.#setActive(index);
  }

  /** @param {number} index */
  #select(index) {
    const { options, value, input } = this.refs;
    const option = options[index];
    if (!option) return;

    for (const item of options) {
      item.setAttribute('aria-selected', String(item === option));
    }

    const size = option.dataset.value ?? '';
    value.textContent = size;
    input.value = size;
    input.disabled = false;
    this.setAttribute('data-selected', '');
    this.#rememberSessionSize(size);
  }

  /** @returns {Record<string, string>} */
  #readSessionSizes() {
    try {
      const sizes = JSON.parse(sessionStorage.getItem(SESSION_SIZES_KEY) ?? '{}');
      return sizes && typeof sizes === 'object' ? sizes : {};
    } catch {
      return {};
    }
  }

  /** @param {string} size */
  #rememberSessionSize(size) {
    const { productId } = this.dataset;
    if (!productId || !size) return;

    const sizes = this.#readSessionSizes();
    sizes[productId] = size;
    try {
      sessionStorage.setItem(SESSION_SIZES_KEY, JSON.stringify(sizes));
    } catch {
      // Storage can be unavailable (private mode). The size then just isn't remembered.
    }
  }

  /** Selects the size picked for this product earlier in this tab, if any. */
  #restoreSessionSize() {
    const { productId } = this.dataset;
    const saved = productId ? this.#readSessionSizes()[productId] : undefined;
    if (!saved) return;

    const index = this.refs.options.findIndex((option) => option.dataset.value === saved);
    if (index >= 0) this.#select(index);
  }

  /**
   * Switching 18K / 14K opens the other product. Hand the chosen size over to it, once.
   * @param {Event} event
   */
  #onKaratSwitch = (event) => {
    const input = event.target;
    const { linkedProductId } = this.dataset;
    if (!(input instanceof HTMLInputElement) || !input.dataset.url || !linkedProductId) return;
    if (!input.closest('gold-karat-selector') || !this.#root.contains(input) || !this.selectedValue) return;

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ size: this.selectedValue, productId: linkedProductId }));
    } catch {
      // Storage can be unavailable (private mode). The size then just isn't carried over.
    }
  };

  /** Picks up a size handed over by the 18K / 14K counterpart. Otherwise the placeholder stays. */
  #restoreSize() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null');
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      return;
    }
    if (!saved || saved.productId !== this.dataset.productId) return;

    const index = this.refs.options.findIndex((option) => option.dataset.value === saved.size);
    if (index >= 0) this.#select(index);
  }

  /**
   * The ring sizer app returns to the product page with ?ring_size=<EU size>. Select that size (if it is
   * in this block's range) and drop the parameter, so a reload or a shared link doesn't pick it again.
   * The confirmation popup still runs before the ring goes in the cart.
   */
  #applySizeFromUrl() {
    if (this.closest('.quick-add-modal__content')) return;

    const url = new URL(window.location.href);
    const requested = url.searchParams.get(SIZE_PARAM)?.trim();
    if (!requested) return;

    url.searchParams.delete(SIZE_PARAM);
    history.replaceState(history.state, '', url);

    if (!/^\d+$/.test(requested)) return;
    const index = this.refs.options.findIndex(
      (option) => (option.dataset.value ?? '').replace(/\D/g, '') === requested
    );
    if (index >= 0) this.#select(index);
  }
}

if (!customElements.get('ring-size-picker')) {
  customElements.define('ring-size-picker', RingSizePicker);
}
